import { useState } from "react";
import {
  BookOpen,
  Settings,
  Database,
  Calculator,
  Code,
  ChevronRight,
  Users,
  GraduationCap,
  ClipboardList,
  Link2,
  BarChart3,
  PieChart,
  Eye,
} from "lucide-react";
import { cn } from "../utils/cn";

type WikiSection =
  | "overview"
  | "architecture"
  | "datamodel"
  | "features"
  | "calculations"
  | "development"
  | "faq"
  | "roadmap";

const Wiki = () => {
  const [activeSection, setActiveSection] = useState<WikiSection>("overview");

  const sections: {
    id: WikiSection;
    label: string;
    icon: React.ElementType;
  }[] = [
    { id: "overview", label: "Überblick", icon: BookOpen },
    { id: "architecture", label: "Architektur", icon: Settings },
    { id: "datamodel", label: "Datenmodell", icon: Database },
    { id: "features", label: "Features", icon: ClipboardList },
    { id: "calculations", label: "Berechnungslogik", icon: Calculator },
    { id: "development", label: "Entwicklung", icon: Code },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewSection />;
      case "architecture":
        return <ArchitectureSection />;
      case "datamodel":
        return <DatamodelSection />;
      case "features":
        return <FeaturesSection />;
      case "calculations":
        return <CalculationsSection />;
      case "development":
        return <DevelopmentSection />;
      case "faq":
        return <FAQSection />;
      default:
        return <OverviewSection />;
    }
  };

  return (
    <div className="flex gap-6">
      {/* Sidebar Navigation */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sticky top-24">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Dokumentation
          </h3>
          <nav className="space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    "w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Icon
                    className={cn("w-4 h-4", isActive && "text-primary-600")}
                  />
                  <span>{section.label}</span>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 ml-auto text-primary-600" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          {renderSection()}
        </div>
      </div>
    </div>
  );
};

// Section Components

const OverviewSection = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        Adjunkty - Schulplanungssystem
      </h1>
      <p className="text-gray-600">Digitale Stundenzuteilung für Gymnasien</p>
    </div>

    <div className="prose prose-gray max-w-none">
      <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">
        Was ist Adjunkty?
      </h2>
      <p className="text-gray-600 mb-4">
        Adjunkty ist ein modernes Schulplanungssystem zur Verwaltung von
        Stundenzuteilungen an Gymnasien. Es ermöglicht:
      </p>
      <ul className="space-y-2 text-gray-600">
        <li className="flex items-start">
          <span className="text-primary-600 mr-2">•</span>
          Effiziente Verwaltung von Lehrpersonen, Klassen und Fächern
        </li>
        <li className="flex items-start">
          <span className="text-primary-600 mr-2">•</span>
          Automatische Berechnung von Arbeitslasten und Entschädigungsstunden
        </li>
        <li className="flex items-start">
          <span className="text-primary-600 mr-2">•</span>
          Echtzeit-Monitoring der Workload-Balance
        </li>
        <li className="flex items-start">
          <span className="text-primary-600 mr-2">•</span>
          Inline-Editing im Notion-Stil für schnelle Anpassungen
        </li>
        <li className="flex items-start">
          <span className="text-primary-600 mr-2">•</span>
          Umfassende Reports und Analysen
        </li>
      </ul>

      <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">
        Technologie-Stack
      </h2>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Frontend</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>React 18.3.1 + TypeScript</li>
            <li>Vite 6.x</li>
            <li>Tailwind CSS 3.4.0</li>
          </ul>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Libraries</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>Lucide React (Icons)</li>
            <li>Recharts (Charts)</li>
            <li>LocalStorage (Daten)</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const ArchitectureSection = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-gray-900">Architektur</h1>

    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          State Management
        </h2>
        <p className="text-gray-600 mb-4">
          DataContext (Context API) verwaltet den globalen State:
        </p>
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-gray-100">
            <code>{`{
  lehrpersonen: Lehrperson[]
  klassen: Klasse[]
  faecher: Fach[]
  zuteilungen: Zuteilung[]
  gekoppelteKlassen: GekoppelteKlasse[]
}`}</code>
          </pre>
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          CRUD-Operationen
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Lehrpersonen</h4>
            <code className="text-xs text-gray-600">
              add / delete / update
            </code>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Klassen</h4>
            <code className="text-xs text-gray-600">
              add / delete / update
            </code>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Fächer</h4>
            <code className="text-xs text-gray-600">
              add / delete / update
            </code>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Zuteilungen</h4>
            <code className="text-xs text-gray-600">
              add / delete / update
            </code>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Alle Änderungen werden automatisch in localStorage persistiert.
        </p>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Component Architecture
        </h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-600 mb-2">
            <strong>Layout:</strong> App.tsx mit Sidebar Navigation + Tab
            Routing
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Shared:</strong> InlineSelect, Button, Input, Modal
          </p>
          <p className="text-gray-600">
            <strong>Pattern:</strong> Expandable Rows, Inline Editing, Live
            Updates
          </p>
        </div>
      </div>
    </div>
  </div>
);

const DatamodelSection = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-gray-900">Datenmodell</h1>

    {/* Entity Relationship Diagram */}
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Database className="w-5 h-5 mr-2 text-blue-600" />
        Entity Relationship Modell
      </h2>
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <svg
          viewBox="0 0 800 500"
          className="w-full h-auto"
          style={{ maxHeight: "500px" }}
        >
          {/* Lehrperson Entity */}
          <g>
            <rect
              x="50"
              y="50"
              width="160"
              height="100"
              fill="#3B82F6"
              stroke="#1E40AF"
              strokeWidth="2"
              rx="4"
            />
            <text x="130" y="75" textAnchor="middle" fill="white" fontWeight="bold">
              Lehrperson
            </text>
            <line x1="50" y1="85" x2="210" y2="85" stroke="white" strokeWidth="1" />
            <text x="60" y="100" fill="white" fontSize="12">
              id (PK)
            </text>
            <text x="60" y="115" fill="white" fontSize="12">
              name
            </text>
            <text x="60" y="130" fill="white" fontSize="12">
              solllektionen
            </text>
          </g>

          {/* Klasse Entity */}
          <g>
            <rect
              x="320"
              y="50"
              width="160"
              height="110"
              fill="#10B981"
              stroke="#059669"
              strokeWidth="2"
              rx="4"
            />
            <text x="400" y="75" textAnchor="middle" fill="white" fontWeight="bold">
              Klasse
            </text>
            <line x1="320" y1="85" x2="480" y2="85" stroke="white" strokeWidth="1" />
            <text x="330" y="100" fill="white" fontSize="12">
              name (PK)
            </text>
            <text x="330" y="115" fill="white" fontSize="12">
              stufe
            </text>
            <text x="330" y="130" fill="white" fontSize="12">
              profil
            </text>
            <text x="330" y="145" fill="white" fontSize="12">
              bestand
            </text>
          </g>

          {/* Fach Entity */}
          <g>
            <rect
              x="590"
              y="50"
              width="160"
              height="80"
              fill="#F59E0B"
              stroke="#D97706"
              strokeWidth="2"
              rx="4"
            />
            <text x="670" y="75" textAnchor="middle" fill="white" fontWeight="bold">
              Fach
            </text>
            <line x1="590" y1="85" x2="750" y2="85" stroke="white" strokeWidth="1" />
            <text x="600" y="100" fill="white" fontSize="12">
              kuerzel (PK)
            </text>
            <text x="600" y="115" fill="white" fontSize="12">
              name
            </text>
          </g>

          {/* Zuteilung Entity (Junction Table) */}
          <g>
            <rect
              x="320"
              y="250"
              width="160"
              height="140"
              fill="#A855F7"
              stroke="#7E22CE"
              strokeWidth="2"
              rx="4"
            />
            <text x="400" y="275" textAnchor="middle" fill="white" fontWeight="bold">
              Zuteilung
            </text>
            <line x1="320" y1="285" x2="480" y2="285" stroke="white" strokeWidth="1" />
            <text x="330" y="300" fill="white" fontSize="12">
              lpId (FK)
            </text>
            <text x="330" y="315" fill="white" fontSize="12">
              klasseId (FK)
            </text>
            <text x="330" y="330" fill="white" fontSize="12">
              fachId (FK)
            </text>
            <text x="330" y="345" fill="white" fontSize="12">
              typ
            </text>
            <text x="330" y="360" fill="white" fontSize="12">
              lektionen
            </text>
            <text x="330" y="375" fill="white" fontSize="12">
              faktor
            </text>
          </g>

          {/* GekoppelteKlasse Entity */}
          <g>
            <rect
              x="50"
              y="300"
              width="180"
              height="100"
              fill="#EF4444"
              stroke="#B91C1C"
              strokeWidth="2"
              rx="4"
            />
            <text x="140" y="325" textAnchor="middle" fill="white" fontWeight="bold">
              GekoppelteKlasse
            </text>
            <line x1="50" y1="335" x2="230" y2="335" stroke="white" strokeWidth="1" />
            <text x="60" y="350" fill="white" fontSize="12">
              klasse1Id (FK)
            </text>
            <text x="60" y="365" fill="white" fontSize="12">
              klasse2Id (FK)
            </text>
            <text x="60" y="380" fill="white" fontSize="12">
              fachKuerzel
            </text>
            <text x="60" y="395" fill="white" fontSize="12">
              schuelerTotal
            </text>
          </g>

          {/* Relationships */}
          {/* Lehrperson to Zuteilung */}
          <path
            d="M 130 150 L 130 200 L 320 200 L 320 250"
            stroke="#64748B"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
          <text x="180" y="195" fill="#475569" fontSize="12" fontWeight="bold">
            1:N
          </text>

          {/* Klasse to Zuteilung */}
          <path
            d="M 400 160 L 400 250"
            stroke="#64748B"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
          <text x="410" y="205" fill="#475569" fontSize="12" fontWeight="bold">
            1:N
          </text>

          {/* Fach to Zuteilung */}
          <path
            d="M 670 130 L 670 200 L 480 200 L 480 250"
            stroke="#64748B"
            strokeWidth="2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
          <text x="580" y="195" fill="#475569" fontSize="12" fontWeight="bold">
            1:N
          </text>

          {/* Klasse to GekoppelteKlasse (2 relationships) */}
          <path
            d="M 320 105 L 280 105 L 280 350 L 230 350"
            stroke="#64748B"
            strokeWidth="2"
            fill="none"
            strokeDasharray="5,5"
            markerEnd="url(#arrowhead)"
          />
          <text x="255" y="230" fill="#475569" fontSize="12" fontWeight="bold">
            N:M
          </text>

          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#64748B" />
            </marker>
          </defs>
        </svg>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-0.5 bg-gray-600"></div>
            <span className="text-gray-600">Direkte Beziehung (1:N)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-0.5 bg-gray-600 border-dashed" style={{ borderTop: "2px dashed #64748B", height: 0 }}></div>
            <span className="text-gray-600">Indirekte Beziehung (N:M)</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 font-mono text-xs">PK</span>
            <span className="text-gray-600">Primary Key</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 font-mono text-xs">FK</span>
            <span className="text-gray-600">Foreign Key</span>
          </div>
        </div>
      </div>
    </div>

    <div className="space-y-6">
      {/* Lehrperson */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
          <Users className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">Lehrperson</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Feld</th>
                <th className="pb-2">Typ</th>
                <th className="pb-2">Beschreibung</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td className="py-1 font-mono text-xs">id</td>
                <td className="py-1">string</td>
                <td className="py-1">LP_timestamp_random</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">name</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "Herzog"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">faecher</td>
                <td className="py-1">string[]</td>
                <td className="py-1">z.B. ["D", "BG"]</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">solllektionen</td>
                <td className="py-1">number</td>
                <td className="py-1">Vertragl. Lektionen (z.B. 20, 26)</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">geburtsdatum</td>
                <td className="py-1">string?</td>
                <td className="py-1">ISO-Datum für Altersentlastung</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">bemerkungen</td>
                <td className="py-1">string?</td>
                <td className="py-1">Optional: Notizen</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Klasse */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
          <GraduationCap className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">Klasse</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Feld</th>
                <th className="pb-2">Typ</th>
                <th className="pb-2">Beschreibung</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td className="py-1 font-mono text-xs">id</td>
                <td className="py-1">string</td>
                <td className="py-1">KL_timestamp_random</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">name</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "1a", "3c"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">stufe</td>
                <td className="py-1">number</td>
                <td className="py-1">1-6</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">profil</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "US", "L/E"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">bestand</td>
                <td className="py-1">number</td>
                <td className="py-1">Schüleranzahl</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">semester</td>
                <td className="py-1">"HS" | "FS"</td>
                <td className="py-1">Herbst-/Frühlingssemester</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">schuljahr</td>
                <td className="py-1">number</td>
                <td className="py-1">z.B. 2024</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">klassenlehrerId</td>
                <td className="py-1">string | null</td>
                <td className="py-1">Referenz auf Lehrperson</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Fach */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
          <BookOpen className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-gray-900">Fach</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Feld</th>
                <th className="pb-2">Typ</th>
                <th className="pb-2">Beschreibung</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td className="py-1 font-mono text-xs">id</td>
                <td className="py-1">string</td>
                <td className="py-1">Eindeutige ID</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">kuerzel</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "D", "BG", "M"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">name</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "Deutsch", "Mathematik"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">pflichtfach</td>
                <td className="py-1">FachStufeConfig</td>
                <td className="py-1">Stufen 1-6: true/false</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">freifach</td>
                <td className="py-1">FachStufeConfig</td>
                <td className="py-1">Stufen 1-6: true/false</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">hk</td>
                <td className="py-1">FachStufeConfig</td>
                <td className="py-1">HK-Verfügbarkeit pro Stufe</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">profileConfig</td>
                <td className="py-1">object?</td>
                <td className="py-1">Profilspezifische Überschreibungen</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-3">
            FachStufeConfig: {`{ 1: bool, 2: bool, 3: bool, 4: bool, 5: bool, 6: bool }`}
          </p>
        </div>
      </div>

      {/* Zuteilung */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
          <ClipboardList className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Zuteilung</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Feld</th>
                <th className="pb-2">Typ</th>
                <th className="pb-2">Beschreibung</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td className="py-1 font-mono text-xs">id</td>
                <td className="py-1">string</td>
                <td className="py-1">Eindeutige ID</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">lpId</td>
                <td className="py-1">string | null</td>
                <td className="py-1">Lehrperson ID (optional)</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">klasseId</td>
                <td className="py-1">string</td>
                <td className="py-1">Klasse ID</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">fachId</td>
                <td className="py-1">string</td>
                <td className="py-1">Fach ID</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">typ</td>
                <td className="py-1">enum</td>
                <td className="py-1">Standard | HK | Praktikum | SHT | Pool</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">lektionen</td>
                <td className="py-1">number</td>
                <td className="py-1">z.B. 2, 3, 4.5</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">hatHk</td>
                <td className="py-1">boolean</td>
                <td className="py-1">Hat Hilfskraft-Unterstützung</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">entschaedigungFaktor</td>
                <td className="py-1">number</td>
                <td className="py-1">Automatisch berechnet</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">entschaedigungStunden</td>
                <td className="py-1">number</td>
                <td className="py-1">Berechnete Stunden</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">bemerkungen</td>
                <td className="py-1">string?</td>
                <td className="py-1">Optional: Notizen</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* GekoppelteKlasse */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center space-x-2">
          <Link2 className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold text-gray-900">GekoppelteKlasse</h3>
        </div>
        <div className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Feld</th>
                <th className="pb-2">Typ</th>
                <th className="pb-2">Beschreibung</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td className="py-1 font-mono text-xs">id</td>
                <td className="py-1">string</td>
                <td className="py-1">Eindeutige ID</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">klasse1Id</td>
                <td className="py-1">string</td>
                <td className="py-1">Erste Klasse</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">klasse2Id</td>
                <td className="py-1">string</td>
                <td className="py-1">Zweite Klasse</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">fachKuerzel</td>
                <td className="py-1">string</td>
                <td className="py-1">z.B. "BG"</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">schuelerTotal</td>
                <td className="py-1">number</td>
                <td className="py-1">Gesamtanzahl Schüler</td>
              </tr>
              <tr>
                <td className="py-1 font-mono text-xs">bemerkungen</td>
                <td className="py-1">string?</td>
                <td className="py-1">Optional: Notizen</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

const FeaturesSection = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-gray-900">Features</h1>

    <div className="grid gap-4">
      {/* Dashboard */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900">Dashboard</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• KPI-Cards: Lehrpersonen, Klassen, Zuteilungen, Lektionen</li>
          <li>• Balkendiagramm: Arbeitsbelastung (Soll vs. Ist)</li>
          <li>• Kreisdiagramm: Workload-Verteilung</li>
          <li>• Sortierte Tabelle mit Status-Badges</li>
        </ul>
      </div>

      {/* Lehrpersonen */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Users className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-gray-900">Lehrpersonen</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Expandable Rows mit allen Zuteilungen</li>
          <li>• Inline-Editing aller Felder</li>
          <li>• Live-Berechnung bei Änderungen</li>
          <li>• Quick-Add für neue Zuteilungen im Kontext</li>
        </ul>
      </div>

      {/* Klassen */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <GraduationCap className="w-5 h-5 text-green-600" />
          <h3 className="font-semibold text-gray-900">Klassen</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Klassenlehrer-Zuweisung via Dropdown</li>
          <li>• Expandable Rows: Wer unterrichtet was?</li>
          <li>• Stufe, Profil, Bestand, Semester</li>
        </ul>
      </div>

      {/* Stundenzuteilung */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <ClipboardList className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Stundenzuteilung</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Globale Übersichtstabelle aller Zuteilungen</li>
          <li>• Automatische Entschädigungsberechnung</li>
          <li>• Inline-Editing: LP, Klasse, Fach, Typ, Lektionen</li>
        </ul>
      </div>

      {/* Fächer */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <BookOpen className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-gray-900">Fächer</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Fächerverwaltung mit Stufen-Konfiguration</li>
          <li>• Pflichtfach / Freifach / HK pro Stufe</li>
          <li>• Profilspezifische Überschreibungen</li>
          <li>• Klick auf Fach öffnet Detailansicht</li>
        </ul>
      </div>

      {/* Fach-Detailansicht */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Eye className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-gray-900">Fach-Detailansicht</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Alle Zuteilungen für ein Fach</li>
          <li>• Gefiltert nach Klasse und Lehrperson</li>
          <li>• Übersicht der Gesamtlektionen</li>
        </ul>
      </div>

      {/* Gekoppelte Klassen */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Link2 className="w-5 h-5 text-rose-600" />
          <h3 className="font-semibold text-gray-900">Gekoppelte Klassen</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Verbindung von zwei Klassen für ein Fach</li>
          <li>• Automatische Schülerzahl-Berechnung</li>
          <li>• Verwaltung von Klassenkooperationen</li>
        </ul>
      </div>

      {/* Reports */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <BarChart3 className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold text-gray-900">Berichte & Analysen</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Übersicht: KPIs + Charts</li>
          <li>• Arbeitsbelastung: Detaillierte LP-Analyse</li>
          <li>• Klassen: Statistiken + Zuteilungsanzahl</li>
          <li>• Fächer: Nutzungsstatistiken</li>
        </ul>
      </div>

      {/* Charts (Test) */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <PieChart className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-900">Charts (Test)</h3>
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Recharts-Visualisierungen Testseite</li>
          <li>• Verschiedene Chart-Typen (Balken, Kreis, Linien)</li>
          <li>• Interaktive Diagramme mit Tooltips</li>
        </ul>
      </div>
    </div>
  </div>
);

const CalculationsSection = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-gray-900">Berechnungslogik</h1>

    <div className="space-y-6">
      {/* Soll-Stunden */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Soll-Lektionen</h3>
        <div className="bg-white rounded p-3 font-mono text-sm mb-2">
          soll = vertraglich zugesicherte Lektionen
        </div>
        <div className="bg-white rounded p-3 font-mono text-sm mb-2">
          Bei Altersentlastung (≥57): effectiveSoll = vertr. Lektionen × (basislektionenAE / basislektionen)
        </div>
        <p className="text-sm text-blue-800">
          Beispiel: 26 vertr. Lektionen, Basis 26/24 AE → effektiv 24
        </p>
      </div>

      {/* Ist-Stunden */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 mb-2">Ist-Stunden</h3>
        <div className="bg-white rounded p-3 font-mono text-sm mb-2">
          ist = Σ(alle Lektionen dieser Lehrperson)
        </div>
        <p className="text-sm text-green-800">
          Summe aller zugeteilten Lektionen
        </p>
      </div>

      {/* Entschädigungsfaktor */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-semibold text-purple-900 mb-3">
          Entschädigungsfaktor
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between bg-white rounded p-2">
            <span className="text-purple-800">Kleine Klasse (&lt; 7 Schüler)</span>
            <span className="font-mono font-semibold">× 0.75</span>
          </div>
          <div className="flex items-center justify-between bg-white rounded p-2">
            <span className="text-purple-800">Standard</span>
            <span className="font-mono font-semibold">× 1.00</span>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Status-Berechnung</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Unterbelastet
            </span>
            <span className="text-sm text-gray-600">Differenz &lt; -2</span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Ausgeglichen
            </span>
            <span className="text-sm text-gray-600">-2 ≤ Differenz ≤ +2</span>
          </div>
          <div className="flex items-center space-x-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Überbelastet
            </span>
            <span className="text-sm text-gray-600">Differenz &gt; +2</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Toleranz: ±2 Lektionen gelten als ausgeglichen
        </p>
      </div>
    </div>
  </div>
);

const DevelopmentSection = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-bold text-gray-900">Entwicklung</h1>

    <div className="space-y-6">
      {/* Commands */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Development Commands
        </h2>
        <div className="bg-gray-900 rounded-lg p-4 space-y-2">
          <div className="flex items-center space-x-3">
            <code className="text-green-400">yarn dev</code>
            <span className="text-gray-400 text-sm">
              → Dev Server (localhost:3000)
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <code className="text-green-400">yarn build</code>
            <span className="text-gray-400 text-sm">→ Production Build</span>
          </div>
          <div className="flex items-center space-x-3">
            <code className="text-green-400">yarn preview</code>
            <span className="text-gray-400 text-sm">→ Build Preview</span>
          </div>
        </div>
      </div>

      {/* Project Structure */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Projektstruktur
        </h2>
        <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm">
          <pre className="text-gray-700">{`src/
├── components/     # React-Komponenten
│   ├── Dashboard.tsx
│   ├── Lehrpersonen.tsx
│   ├── Klassen.tsx
│   └── ...
├── context/
│   └── DataContext.tsx  # State Management
├── types/
│   └── index.ts    # TypeScript Interfaces
├── utils/
│   ├── calculations.ts
│   ├── storage.ts
│   └── demoData.ts
└── App.tsx         # Main App`}</pre>
        </div>
      </div>

      {/* Best Practices */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Best Practices
        </h2>
        <ul className="space-y-2 text-gray-600">
          <li className="flex items-start">
            <span className="text-green-600 mr-2">✓</span>
            TypeScript Strict Mode aktiviert
          </li>
          <li className="flex items-start">
            <span className="text-green-600 mr-2">✓</span>
            Alle Props typisiert
          </li>
          <li className="flex items-start">
            <span className="text-green-600 mr-2">✓</span>
            Utility-First CSS mit Tailwind
          </li>
          <li className="flex items-start">
            <span className="text-green-600 mr-2">✓</span>
            Context für globalen State
          </li>
          <li className="flex items-start">
            <span className="text-green-600 mr-2">✓</span>
            LocalStorage Persistence
          </li>
        </ul>
      </div>
    </div>
  </div>
);

const FAQSection = () => {
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (id: string) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const faqs = [
    {
      id: "storage",
      q: "Wo werden die Daten gespeichert?",
      a: "Im Browser (localStorage). Daten bleiben auch nach Reload erhalten, sind aber Browser-spezifisch.",
    },
    {
      id: "multiuser",
      q: "Können mehrere Benutzer gleichzeitig arbeiten?",
      a: "Nein, aktuell Single-User pro Browser. Für Multi-User wäre Backend + Datenbank nötig.",
    },
    {
      id: "cache",
      q: "Was passiert wenn ich den Browser-Cache lösche?",
      a: "Alle Daten gehen verloren! Backup-Funktion wird empfohlen.",
    },
    {
      id: "soll",
      q: "Wie wird Soll berechnet?",
      a: "Soll = vertraglich zugesicherte Lektionen (direkt eingegeben). Bei Altersentlastung (≥57): proportional reduziert anhand der Basislektionen des Fachs.",
    },
    {
      id: "faktor",
      q: "Wann greift der 0.75 Faktor?",
      a: "Bei Klassen mit weniger als 7 Schülern.",
    },
    {
      id: "status",
      q: "Wie funktioniert der Status (grün/gelb/rot)?",
      a: "Differenz < -2: Unterbelastet (gelb), > 2: Überbelastet (rot), dazwischen: Ausgeglichen (grün).",
    },
    {
      id: "undo",
      q: "Gibt es eine Undo-Funktion?",
      a: "Nein, aktuell nicht. Löschungen sind final (mit Bestätigung).",
    },
    {
      id: "offline",
      q: "Kann ich die App offline nutzen?",
      a: "Ja, nach einmaligem Laden. Keine Server-Verbindung nötig.",
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">FAQ</h1>

      <div className="space-y-2">
        {faqs.map((faq) => (
          <div
            key={faq.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleItem(faq.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900">{faq.q}</span>
              <ChevronRight
                className={cn(
                  "w-5 h-5 text-gray-400 transition-transform",
                  openItems.includes(faq.id) && "rotate-90"
                )}
              />
            </button>
            {openItems.includes(faq.id) && (
              <div className="px-4 pb-3 text-gray-600 text-sm border-t border-gray-100 pt-3 bg-gray-50">
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Wiki;
